'use client';

import { useEffect, useRef } from 'react';

/**
 * 배너 높이를 `--banner-height` 로 내보낸다.
 *
 * 본문은 헤더(sticky)가 배너만큼 자리를 차지해 이미 밀려 있다 — 여기서 레이아웃을 바꾸지
 * 않는다. 다만 뷰포트 기준 오프셋(앵커 scroll-margin, sticky 레일, 고정 토스트)은 헤더가
 * 배너만큼 두꺼워진 걸 모르므로 이 값을 더해 쓴다. 첫 값은 배너 바로 뒤 인라인 스크립트
 * (BANNER_HEIGHT_SCRIPT)가 본문 파싱 전에 쓰고, 이 훅은 이후 높이 변화(폰트 교체·창 크기·
 * 닫기)를 따라간다.
 */
function setBannerHeight(height: number) {
  const value = Math.max(height, 0);

  document.documentElement.style.setProperty('--banner-height', `${value}px`);
}

export function useBannerHeightSync(isVisible: boolean) {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bannerElement = bannerRef.current;

    if (!bannerElement || !isVisible) {
      setBannerHeight(0);
      return;
    }

    const syncHeight = () => setBannerHeight(bannerElement.offsetHeight);
    syncHeight();

    let rafId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncHeight);
    });
    observer.observe(bannerElement);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      setBannerHeight(0);
    };
  }, [isVisible]);

  return bannerRef;
}
